import { useState, type ReactNode } from 'react';
import { t } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import styles from './settings.module.css';

/**
 * The window a Settings list adds in (decision 2803; T1619): the page's own
 * add form moved inside the shared Modal, opened from + Add at the head of the
 * list. 🔨 A page mounts it only while it is open, so each opening starts empty
 * and decision 0604's rule that an add form empties after a save is met by the
 * mount. A refusal reads in a banner inside the window; a save closes it.
 */
export type AddAnswer = { ok: true } | { ok: false; message: string };

export function SettingsAddWindow({ slug, card, title, primaryLabel, valid, invalidReason, submit, onClose, onSaved, children }: {
  /** The window's `data-modal` is `settings-add-<slug>`, and its body carries `data-settings-card` as `add-<slug>` unless `card` says otherwise. */
  slug: string;
  card?: string;
  title: string;
  /** The primary button's words; the title by default. An Edit window drawn on the same frame says Save Changes (decision 10090). */
  primaryLabel?: string;
  /** Whether the form can be submitted yet; the primary stays grayed until it can. */
  valid: boolean;
  /** Why the primary is gray while the form is not valid, which a disabled button always says (decision 0559; T3510). */
  invalidReason?: string;
  submit: () => Promise<AddAnswer>;
  onClose: () => void;
  onSaved: () => void;
  children: ReactNode;
}) {
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    const answer = await submit();
    setBusy(false);
    if (answer.ok) onSaved();
    else setRefusal(answer.message);
  };
  return (
    <Modal
      open
      title={title}
      onCancel={onClose}
      dataModal={`settings-add-${slug}`}
      actions={[
        { kind: 'primary', label: primaryLabel ?? title, onAct: () => void add(), disabled: !valid || busy, ...(invalidReason !== undefined && !valid ? { disabledReason: invalidReason } : {}) },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onClose },
      ]}
    >
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      <div className={styles.addWindowBody} data-settings-card={card ?? `add-${slug}`}>{children}</div>
    </Modal>
  );
}
